/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { FastForward, PlayIcon, SkipForward } from 'lucide-react';
import { FC, useState, useEffect, useRef, KeyboardEvent } from 'react';

import { Inactivity } from './useRunViewerVideo';

const ThreeWayToggle: FC<{ defaultValue: Inactivity; onChange: (arg0: Inactivity) => void }> = ({
    defaultValue,
    onChange,
}) => {
    const [selected, setSelected] = useState(defaultValue);
    const [sliderStyle, setSliderStyle] = useState({});
    const sliderRef = useRef<HTMLLabelElement>(null);

    useEffect(() => {
        if (sliderRef.current) {
            const { offsetWidth, offsetLeft } = sliderRef.current;
            setSliderStyle({
                width: offsetWidth,
                transform: `translateX(${offsetLeft}px)`,
            });
        }
    }, [selected]);

    const handleClick = (value: Inactivity) => {
        setSelected(value);
        onChange(value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLLabelElement>, value: Inactivity) => {
        if (e && e.key === 'Enter') {
            handleClick(value);
        }
    };

    return (
        <div className="relative flex items-center gap-8 text-slate-700">
            <input
                type="radio"
                id="off"
                name="toggle"
                value="off"
                checked={selected === 'off'}
                onChange={() => handleClick('off')}
                className="sr-only"
            />
            <input
                type="radio"
                id="fast-forward"
                name="toggle"
                value="fast-forward"
                checked={selected === 'fast-forward'}
                onChange={() => handleClick('fast-forward')}
                className="sr-only"
            />
            <input
                type="radio"
                id="skip"
                name="toggle"
                value="skip"
                checked={selected === 'skip'}
                onChange={() => handleClick('skip')}
                className="sr-only"
            />
            <label
                htmlFor="off"
                ref={selected === 'off' ? sliderRef : null}
                className={`p-2 cursor-pointer relative z-10 ${selected === 'off' ? 'z-20' : ''} rounded-full focus:outline-blue-500`}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'off')}
                aria-label="The inactivity segments will play in full"
            >
                <PlayIcon size="16" />
            </label>
            <label
                htmlFor="fast-forward"
                ref={selected === 'fast-forward' ? sliderRef : null}
                className={`p-2 cursor-pointer relative z-10 ${selected === 'fast-forward' ? 'z-20' : ''} rounded-full focus:outline-blue-500`}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'fast-forward')}
                aria-label="The inactivity segments will be fast-forwarded"
            >
                <FastForward size="16" />
            </label>
            <label
                htmlFor="skip"
                ref={selected === 'skip' ? sliderRef : null}
                className={`p-2 cursor-pointer relative z-10 ${selected === 'skip' ? 'z-20' : ''} rounded-full focus:outline-blue-500`}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'skip')}
                aria-label="The inactivity segments will be skipped entirely"
            >
                <SkipForward size="16" />
            </label>
            <div
                style={{ ...styles.slider, ...sliderStyle }}
                className="absolute top-0 left-0 h-full rounded-full border-2 bg-slate-200"
            />
        </div>
    );
};

const styles = {
    slider: {
        content: '""',
        transition: 'transform 0.3s ease, width 0.3s ease',
        zIndex: 0,
    },
};

export default ThreeWayToggle;
